import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
    Alert,
    App,
    Button,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Tree,
    Typography,
} from 'antd'
import type { DataNode } from 'antd/es/tree'
import {
    CheckCircleOutlined,
    EditOutlined,
    ExclamationCircleFilled,
    SafetyCertificateOutlined,
    SearchOutlined,
    StopOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

import { PageLayout } from '../../components/layout'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../components/common/FilterBar'
import {
    PaginatedListCard,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../../components/common/PaginatedListTable'
import { useDedupedMessage } from '../../hooks/useDedupedMessage'
import { usePermissions } from '../../hooks/usePermissions'
import { useMutationAction } from '../../hooks/useMutationAction'
import {
    getProjectManagementOverview,
    syncViewerAccountProjects,
    updateViewerAccount,
} from '../../services/projectManagementService'
import type {
    ProjectManagementOverview,
    ProjectManagementProjectItem,
    ViewerAccountStatus,
    ViewerAccountSummary,
} from '../../types/projectManagement'
import type { MutationRequestOptions } from '../../utils/axiosConfig'
import {
    filterRegionSelectOption,
    matchesRegionPathKeyword,
} from '../../utils/regionSearch'
import { normalizeSearchText } from '../../utils/searchText'
import './ProjectViewerScopes.css'

interface ProjectViewerScopesProps {
    embedded?: boolean
}

interface ProjectTreeGroup {
    key: string
    label: string
    fullPath: string
    projects: ProjectManagementProjectItem[]
}

interface ViewerFilters {
    nameKeyword: string
    usernameKeyword: string
    statusFilter: 'all' | ViewerAccountStatus
}

interface AuthorizationFilters {
    projectKeyword: string
    projectRegionFilter: 'all' | string
    projectStatusFilter: 'all' | ViewerAccountStatus
    authorizationFilter: 'all' | 'authorized' | 'unauthorized'
}

interface ViewerStatusModalState {
    open: boolean
    viewer: ViewerAccountSummary | null
    targetStatus: ViewerAccountStatus | null
    submitting: boolean
}

interface MaskedReadonlyInputProps {
    value?: string
    'aria-label'?: string
}

const EMPTY_OVERVIEW: ProjectManagementOverview = {
    projects: [],
    viewerAccounts: [],
}

const EMPTY_STATUS_MODAL: ViewerStatusModalState = {
    open: false,
    viewer: null,
    targetStatus: null,
    submitting: false,
}

const DEFAULT_PAGE_SIZE = 10
const VIEWER_TABLE_SCROLL_X = 1240
const DEFAULT_VIEWER_FILTERS: ViewerFilters = {
    nameKeyword: '',
    usernameKeyword: '',
    statusFilter: 'all',
}
const DEFAULT_AUTHORIZATION_FILTERS: AuthorizationFilters = {
    projectKeyword: '',
    projectRegionFilter: 'all',
    projectStatusFilter: 'all',
    authorizationFilter: 'all',
}

const statusOptions: Array<{ label: string; value: 'all' | ViewerAccountStatus }> = [
    { label: 'all', value: 'all' },
    { label: 'active', value: 'active' },
    { label: 'inactive', value: 'inactive' },
]

const normalizeText = (value?: string | null) =>
    normalizeSearchText(value).toLowerCase()
const REGION_PATH_SEPARATOR = ' / '

const buildRegionPathOptions = (regionPaths: Array<string | null | undefined>): string[] => {
    const options = new Set<string>()

    regionPaths.forEach((regionPath) => {
        const parts = (regionPath || '')
            .split(REGION_PATH_SEPARATOR)
            .map((part) => part.trim())
            .filter(Boolean)

        parts.forEach((_, index) => {
            options.add(parts.slice(0, index + 1).join(REGION_PATH_SEPARATOR))
        })
    })

    return Array.from(options).sort((a, b) => a.localeCompare(b))
}

const matchesRegionPath = (projectRegionPath: string, selectedRegionPath: string): boolean =>
    projectRegionPath === selectedRegionPath ||
    projectRegionPath.startsWith(`${selectedRegionPath}${REGION_PATH_SEPARATOR}`) ||
    matchesRegionPathKeyword(projectRegionPath, selectedRegionPath)

const isDevEnvironment = import.meta.env.DEV

const MaskedReadonlyInput: React.FC<MaskedReadonlyInputProps> = ({
    value,
    'aria-label': ariaLabel,
}) => (
    <div style={{ position: 'relative' }}>
        <Input
            value={value}
            aria-label={ariaLabel}
            disabled
            styles={{
                input: {
                    color: 'rgba(0, 0, 0, 0.45)',
                },
            }}
        />
        <div
            aria-hidden="true"
            style={{
                position: 'absolute',
                inset: 1,
                borderRadius: 5,
                background: 'rgba(255, 255, 255, 0.4)',
                cursor: 'not-allowed',
            }}
        />
    </div>
)

const getLoadFailureMessageKey = (error: unknown): string => {
    if (!axios.isAxiosError(error)) {
        return 'admin:projectV1.messages.loadFailed'
    }

    if (error.code === 'ECONNABORTED') {
        return 'admin:projectV1.messages.loadTimeout'
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
        return 'admin:projectV1.messages.loadUnauthorized'
    }

    return 'admin:projectV1.messages.loadFailed'
}

const buildProjectGroups = (
    projects: ProjectManagementProjectItem[]
): ProjectTreeGroup[] => {
    const groups = new Map<string, ProjectTreeGroup>()
    projects.forEach((project) => {
        const fullPath = project.region || 'Unassigned'
        const label = fullPath.split(' / ').filter(Boolean).at(-1) || fullPath
        if (!groups.has(fullPath)) {
            groups.set(fullPath, {
                key: `region:${fullPath}`,
                label,
                fullPath,
                projects: [],
            })
        }
        groups.get(fullPath)?.projects.push(project)
    })

    return Array.from(groups.values()).sort((a, b) => a.fullPath.localeCompare(b.fullPath))
}

const ProjectViewerScopes: React.FC<ProjectViewerScopesProps> = ({
    embedded = false,
}) => {
    const { message: rawMessage } = App.useApp()
    const message = useDedupedMessage(rawMessage)
    const { t } = useTranslation(['admin', 'navigation', 'common'])
    const {
        canGrantProjectViewers,
        canUpdateProjectViewerAccounts,
        canUpdateProjectViewerAccountStatus,
    } = usePermissions()
    const [overview, setOverview] = useState<ProjectManagementOverview>(EMPTY_OVERVIEW)
    const [loading, setLoading] = useState(false)
    const [nameKeyword, setNameKeyword] = useState('')
    const [usernameKeyword, setUsernameKeyword] = useState('')
    const [statusFilter, setStatusFilter] = useState<'all' | ViewerAccountStatus>('all')
    const [editingViewer, setEditingViewer] = useState<ViewerAccountSummary | null>(null)
    const [authorizationViewer, setAuthorizationViewer] = useState<ViewerAccountSummary | null>(null)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const [authorizationModalOpen, setAuthorizationModalOpen] = useState(false)
    const [projectKeyword, setProjectKeyword] = useState('')
    const [projectRegionFilter, setProjectRegionFilter] = useState<'all' | string>('all')
    const [projectStatusFilter, setProjectStatusFilter] = useState<'all' | ViewerAccountStatus>('all')
    const [authorizationFilter, setAuthorizationFilter] = useState<'all' | 'authorized' | 'unauthorized'>('all')
    const [appliedViewerFilters, setAppliedViewerFilters] =
        useState<ViewerFilters>(DEFAULT_VIEWER_FILTERS)
    const [appliedAuthorizationFilters, setAppliedAuthorizationFilters] =
        useState<AuthorizationFilters>(DEFAULT_AUTHORIZATION_FILTERS)
    const [checkedProjectKeys, setCheckedProjectKeys] = useState<string[]>([])
    const [editForm] = Form.useForm()
    const [statusModal, setStatusModal] = useState<ViewerStatusModalState>(EMPTY_STATUS_MODAL)
    const [viewerCurrentPage, setViewerCurrentPage] = useState(1)
    const [viewerPageSize, setViewerPageSize] = useState(DEFAULT_PAGE_SIZE)

    const resetViewerFilters = useCallback(() => {
        setNameKeyword(DEFAULT_VIEWER_FILTERS.nameKeyword)
        setUsernameKeyword(DEFAULT_VIEWER_FILTERS.usernameKeyword)
        setStatusFilter(DEFAULT_VIEWER_FILTERS.statusFilter)
        setAppliedViewerFilters(DEFAULT_VIEWER_FILTERS)
        setViewerCurrentPage(1)
    }, [])

    const resetAuthorizationFilters = useCallback(() => {
        setProjectKeyword(DEFAULT_AUTHORIZATION_FILTERS.projectKeyword)
        setProjectRegionFilter(
            DEFAULT_AUTHORIZATION_FILTERS.projectRegionFilter
        )
        setProjectStatusFilter(
            DEFAULT_AUTHORIZATION_FILTERS.projectStatusFilter
        )
        setAuthorizationFilter(DEFAULT_AUTHORIZATION_FILTERS.authorizationFilter)
        setAppliedAuthorizationFilters(DEFAULT_AUTHORIZATION_FILTERS)
    }, [])

    const loadOverview = useCallback(async (options?: { preserveProjectKeys?: string[] }) => {
        setLoading(true)
        const startTime = performance.now()
        try {
            const data = await getProjectManagementOverview()
            const preservedProjectKeys = new Set(options?.preserveProjectKeys ?? [])
            setOverview((previous) => {
                if (preservedProjectKeys.size === 0) {
                    return data
                }

                const nextProjectKeys = new Set(
                    data.projects.map((project) => project.projectKey)
                )
                const preservedProjects = previous.projects.filter(
                    (project) =>
                        preservedProjectKeys.has(project.projectKey) &&
                        !nextProjectKeys.has(project.projectKey)
                )

                return preservedProjects.length > 0
                    ? { ...data, projects: [...data.projects, ...preservedProjects] }
                    : data
            })
            if (isDevEnvironment) {
                console.info('[ProjectViewerScopes] overview loaded', {
                    durationMs: Math.round(performance.now() - startTime),
                    projectCount: data.projects.length,
                    viewerAccountCount: data.viewerAccounts.length,
                })
            }
        } catch (error) {
            console.error(error)
            if (isDevEnvironment) {
                console.warn('[ProjectViewerScopes] overview load failed', {
                    durationMs: Math.round(performance.now() - startTime),
                    error,
                })
            }
            message.error(t(getLoadFailureMessageKey(error)))
        } finally {
            setLoading(false)
        }
    }, [message, t])

    useEffect(() => {
        void loadOverview()
    }, [loadOverview])

    useEffect(() => {
        setViewerCurrentPage(1)
    }, [appliedViewerFilters])

    const filteredViewers = useMemo(() => {
        return overview.viewerAccounts.filter((viewer) => {
            const matchesName =
                !normalizeText(appliedViewerFilters.nameKeyword) ||
                normalizeText(viewer.displayName).includes(
                    normalizeText(appliedViewerFilters.nameKeyword)
                )
            const matchesUsername =
                !normalizeText(appliedViewerFilters.usernameKeyword) ||
                normalizeText(viewer.username).includes(
                    normalizeText(appliedViewerFilters.usernameKeyword)
                )
            const matchesStatus =
                appliedViewerFilters.statusFilter === 'all' ||
                viewer.status === appliedViewerFilters.statusFilter
            return matchesName && matchesUsername && matchesStatus
        })
    }, [appliedViewerFilters, overview.viewerAccounts])

    const regionOptions = useMemo(() => {
        return buildRegionPathOptions(
            overview.projects
                .filter((project) => project.parentId == null)
                .map((project) => project.region)
                .filter((region) => Boolean(region))
        )
    }, [overview.projects])

    const rootProjects = useMemo(
        () => overview.projects.filter((project) => project.parentId == null),
        [overview.projects]
    )

    const allProjectKeySet = useMemo(
        () => new Set(rootProjects.map((project) => project.projectKey)),
        [rootProjects]
    )

    const filteredProjects = useMemo(() => {
        const authorizedProjectKeys = new Set(checkedProjectKeys)
        return rootProjects.filter((project) => {
            const matchesKeyword =
                !normalizeText(appliedAuthorizationFilters.projectKeyword) ||
                normalizeText(project.name).includes(
                    normalizeText(appliedAuthorizationFilters.projectKeyword)
                ) ||
                normalizeText(project.projectKey).includes(
                    normalizeText(appliedAuthorizationFilters.projectKeyword)
                )
            const matchesRegion =
                appliedAuthorizationFilters.projectRegionFilter === 'all' ||
                matchesRegionPath(
                    project.region,
                    appliedAuthorizationFilters.projectRegionFilter
                )
            const matchesStatus =
                appliedAuthorizationFilters.projectStatusFilter === 'all' ||
                project.status === appliedAuthorizationFilters.projectStatusFilter
            const isAuthorized = authorizedProjectKeys.has(project.projectKey)
            const matchesAuthorization =
                appliedAuthorizationFilters.authorizationFilter === 'all' ||
                (appliedAuthorizationFilters.authorizationFilter ===
                    'authorized' &&
                    isAuthorized) ||
                (appliedAuthorizationFilters.authorizationFilter ===
                    'unauthorized' &&
                    !isAuthorized)
            return matchesKeyword && matchesRegion && matchesStatus && matchesAuthorization
        })
    }, [
        appliedAuthorizationFilters,
        checkedProjectKeys,
        rootProjects,
    ])

    const filteredProjectKeySet = useMemo(
        () => new Set(filteredProjects.map((project) => project.projectKey)),
        [filteredProjects]
    )

    const projectGroups = useMemo(
        () => buildProjectGroups(filteredProjects),
        [filteredProjects]
    )

    const checkedProjectKeySet = useMemo(
        () => new Set(checkedProjectKeys),
        [checkedProjectKeys]
    )

    const treeData = useMemo<DataNode[]>(() => {
        return projectGroups.map((group) => ({
            key: group.key,
            title: (
                <div style={{ padding: '2px 0' }}>
                    <Space size={10} align="center">
                        <Typography.Text strong>{group.label}</Typography.Text>
                        <Typography.Text type="secondary">
                            {t('admin:projectV1.authorization.groupSelectionCount', {
                                selected: group.projects.filter((project) =>
                                    checkedProjectKeySet.has(project.projectKey)
                                ).length,
                                total: group.projects.length,
                            })}
                        </Typography.Text>
                    </Space>
                    <Typography.Text
                        type="secondary"
                        style={{ display: 'block', fontSize: 12, marginTop: 2 }}
                    >
                        {group.fullPath}
                    </Typography.Text>
                </div>
            ),
            children: group.projects.map((project) => ({
                key: project.projectKey,
                title: (
                    <Space size={8} style={{ padding: '2px 0' }}>
                        <Typography.Text>{project.name}</Typography.Text>
                        <Tag color={project.status === 'active' ? 'green' : 'red'}>
                            {t(`admin:projectV1.status.${project.status}`)}
                        </Tag>
                    </Space>
                ),
                isLeaf: true,
            })),
        }))
    }, [checkedProjectKeySet, projectGroups, t])

    const openEditModal = (viewer: ViewerAccountSummary) => {
        if (!canUpdateProjectViewerAccounts) {
            return
        }
        setEditingViewer(viewer)
        editForm.setFieldsValue({
            display_name: viewer.displayName,
            username: viewer.username,
            organization_name: viewer.organizationName,
        })
        setEditModalOpen(true)
    }

    const openAuthorizationModal = useCallback((viewer: ViewerAccountSummary) => {
        if (!canGrantProjectViewers) {
            return
        }
        setAuthorizationViewer(viewer)
        setCheckedProjectKeys(
            viewer.authorizedProjects
                .map((project) => project.projectKey)
                .filter((projectKey) => allProjectKeySet.has(projectKey))
        )
        resetAuthorizationFilters()
        setAuthorizationModalOpen(true)
    }, [allProjectKeySet, canGrantProjectViewers, resetAuthorizationFilters])

    const submitEdit = useCallback(async (mutationOptions: MutationRequestOptions) => {
        if (!editingViewer) {
            return
        }
        try {
            const values = await editForm.validateFields()
            await updateViewerAccount(editingViewer.userId, {
                display_name: values.display_name,
                username: values.username,
                organization_name: values.organization_name || null,
                status: editingViewer.status,
            }, mutationOptions)
            message.success(t('admin:projectV1.messages.updateSuccess'))
            setEditModalOpen(false)
            await loadOverview()
        } catch (error) {
            console.error(error)
            message.error(t('admin:projectV1.messages.operationFailed'))
        }
    }, [editForm, editingViewer, loadOverview, message, t])
    const { run: handleEditSubmit, running: submittingEdit } = useMutationAction(
        submitEdit,
        {
            idempotencyScope: 'viewer-account-update',
            dedupeKey: () =>
                editingViewer
                    ? `viewer-account:update:${editingViewer.userId}`
                    : undefined,
        }
    )

    const submitAuthorization = useCallback(async (mutationOptions: MutationRequestOptions) => {
        if (!authorizationViewer) {
            return
        }
        const projectKeys = checkedProjectKeys
            .filter((projectKey) => allProjectKeySet.has(projectKey))
            .sort()
        try {
            await syncViewerAccountProjects(authorizationViewer.userId, {
                project_keys: projectKeys,
            }, mutationOptions)
            message.success(t('admin:projectV1.messages.authorizationSaved'))
            setAuthorizationModalOpen(false)
            await loadOverview()
        } catch (error) {
            console.error(error)
            message.error(t('admin:projectV1.messages.operationFailed'))
        }
    }, [
        allProjectKeySet,
        authorizationViewer,
        checkedProjectKeys,
        loadOverview,
        message,
        t,
    ])
    const { run: handleSaveAuthorization, running: submittingAuthorization } =
        useMutationAction(submitAuthorization, {
            idempotencyScope: 'viewer-account-projects',
            dedupeKey: () =>
                authorizationViewer
                    ? `viewer-account-projects:${authorizationViewer.userId}:${checkedProjectKeys
                          .filter((projectKey) => allProjectKeySet.has(projectKey))
                          .sort()
                          .join(',')}`
                    : undefined,
        })

    const openStatusModal = (viewer: ViewerAccountSummary) => {
        if (!canUpdateProjectViewerAccountStatus) {
            return
        }
        setStatusModal({
            open: true,
            viewer,
            targetStatus: viewer.status === 'active' ? 'inactive' : 'active',
            submitting: false,
        })
    }

    const closeStatusModal = () => {
        setStatusModal((current) => (current.submitting ? current : EMPTY_STATUS_MODAL))
    }

    const submitStatus = useCallback(async (mutationOptions: MutationRequestOptions) => {
        if (!statusModal.viewer || !statusModal.targetStatus) {
            return
        }

        setStatusModal((current) => ({ ...current, submitting: true }))
        try {
            await updateViewerAccount(statusModal.viewer.userId, {
                display_name: statusModal.viewer.displayName,
                username: statusModal.viewer.username,
                organization_name: statusModal.viewer.organizationName || null,
                status: statusModal.targetStatus,
            }, mutationOptions)
            message.success(t('admin:projectV1.messages.statusUpdateSuccess'))
            setStatusModal(EMPTY_STATUS_MODAL)
            await loadOverview()
        } catch (error) {
            console.error(error)
            message.error(t('admin:projectV1.messages.operationFailed'))
            setStatusModal(EMPTY_STATUS_MODAL)
        }
    }, [loadOverview, message, statusModal.targetStatus, statusModal.viewer, t])
    const { run: handleToggleStatus, running: submittingStatus } =
        useMutationAction(submitStatus, {
            idempotencyScope: 'viewer-account-status',
            dedupeKey: () =>
                statusModal.viewer && statusModal.targetStatus
                    ? `viewer-account:status:${statusModal.viewer.userId}:${statusModal.targetStatus}`
                    : undefined,
        })

    const statusModalTitle = useMemo(() => {
        if (!statusModal.targetStatus) {
            return ''
        }

        return statusModal.targetStatus === 'inactive'
            ? t('admin:projectV1.statusConfirm.disableTitle')
            : t('admin:projectV1.statusConfirm.enableTitle')
    }, [statusModal.targetStatus, t])

    const statusModalDescription = useMemo(() => {
        if (!statusModal.viewer || !statusModal.targetStatus) {
            return ''
        }

        return statusModal.targetStatus === 'inactive'
            ? t('admin:projectV1.statusConfirm.disableDescription', {
                  username: statusModal.viewer.username,
              })
            : t('admin:projectV1.statusConfirm.enableDescription', {
                  username: statusModal.viewer.username,
              })
    }, [statusModal.targetStatus, statusModal.viewer, t])

    const statusActionLabel = useMemo(() => {
        if (!statusModal.targetStatus) {
            return ''
        }

        return statusModal.targetStatus === 'inactive'
            ? t('admin:projectV1.actions.disableAccount')
            : t('admin:projectV1.actions.enableAccount')
    }, [statusModal.targetStatus, t])

    const applyViewerFilters = () => {
        setAppliedViewerFilters({
            nameKeyword: normalizeSearchText(nameKeyword),
            usernameKeyword: normalizeSearchText(usernameKeyword),
            statusFilter,
        })
        setViewerCurrentPage(1)
        void loadOverview()
    }

    const applyAuthorizationFilters = () => {
        setAppliedAuthorizationFilters({
            projectKeyword: normalizeSearchText(projectKeyword),
            projectRegionFilter,
            projectStatusFilter,
            authorizationFilter,
        })
        void loadOverview({ preserveProjectKeys: checkedProjectKeys })
    }

    const selectFilteredResults = () => {
        const nextChecked = new Set(checkedProjectKeys)
        filteredProjects.forEach((project) => nextChecked.add(project.projectKey))
        setCheckedProjectKeys(Array.from(nextChecked))
    }

    const clearFilteredResults = () => {
        setCheckedProjectKeys((current) =>
            current.filter((projectKey) => !filteredProjectKeySet.has(projectKey))
        )
    }
    const canUseViewerActions =
        canUpdateProjectViewerAccounts ||
        canUpdateProjectViewerAccountStatus ||
        canGrantProjectViewers

    const content = (
        <>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message={t('admin:projectV1.notice')}
                    style={{ marginBottom: 16 }}
                />
                <FilterBar data-testid="project-viewer-filter-panel">
                    <FilterField
                        label={t('admin:projectV1.filters.displayNameLabel')}
                    >
                        <Input
                            allowClear
                            value={nameKeyword}
                            placeholder={t('admin:projectV1.filters.displayName')}
                            onChange={(event) => setNameKeyword(event.target.value)}
                            onPressEnter={applyViewerFilters}
                        />
                    </FilterField>
                    <FilterField
                        label={t('admin:projectV1.filters.usernameLabel')}
                    >
                        <Input
                            allowClear
                            value={usernameKeyword}
                            placeholder={t('admin:projectV1.filters.username')}
                            onChange={(event) => setUsernameKeyword(event.target.value)}
                            onPressEnter={applyViewerFilters}
                        />
                    </FilterField>
                    <FilterField
                        label={t('admin:projectV1.filters.statusLabel')}
                    >
                        <Select
                            value={statusFilter}
                            options={statusOptions.map((option) => ({
                                value: option.value,
                                label:
                                    option.value === 'all'
                                        ? t('admin:projectV1.filters.allStatus')
                                        : t(`admin:projectV1.status.${option.value}`),
                            }))}
                            onChange={(value) => setStatusFilter(value)}
                        />
                    </FilterField>
                    <FilterActions testId="project-viewer-filter-actions">
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={applyViewerFilters}
                        >
                            {t('common:buttons.search')}
                        </Button>
                        <Button onClick={resetViewerFilters}>
                            {t('admin:projectV1.actions.resetFilters')}
                        </Button>
                    </FilterActions>
                </FilterBar>

                <PaginatedListCard>
                    <Table<ViewerAccountSummary>
                        rowKey="userId"
                        loading={loading}
                        dataSource={filteredViewers}
                        size={PAGINATED_LIST_TABLE_SIZE}
                        tableLayout="fixed"
                        scroll={{ x: VIEWER_TABLE_SCROLL_X }}
                        pagination={buildPaginatedListPagination({
                            current: viewerCurrentPage,
                            pageSize: viewerPageSize,
                            showTotal: (total) =>
                                t('admin:projectV1.pagination.totalViewers', { total }),
                            onChange: (page, nextPageSize) => {
                                const resolvedPageSize = nextPageSize ?? viewerPageSize
                                setViewerPageSize(resolvedPageSize)
                                setViewerCurrentPage(resolvedPageSize !== viewerPageSize ? 1 : page)
                            },
                        })}
                        columns={[
                        {
                            title: t('admin:projectV1.columns.displayName'),
                            dataIndex: 'displayName',
                            width: 180,
                            ellipsis: true,
                            render: renderPaginatedListText,
                        },
                        {
                            title: t('admin:projectV1.columns.username'),
                            dataIndex: 'username',
                            width: 160,
                            ellipsis: true,
                            render: renderPaginatedListText,
                        },
                        {
                            title: t('admin:projectV1.columns.organizationName'),
                            dataIndex: 'organizationName',
                            width: 220,
                            ellipsis: true,
                            render: renderPaginatedListText,
                        },
                        {
                            title: t('admin:projectV1.columns.authorizedProjectCount'),
                            key: 'authorizedProjectCount',
                            width: 180,
                            render: (_, record) =>
                                t('admin:projectV1.columns.authorizedProjectCountValue', {
                                    count: record.authorizedProjects.length,
                                }),
                        },
                        {
                            title: t('admin:projectV1.columns.status'),
                            dataIndex: 'status',
                            width: 120,
                            render: (value: ViewerAccountStatus) => (
                                <Tag color={value === 'active' ? 'green' : 'red'}>
                                    {t(`admin:projectV1.status.${value}`)}
                                </Tag>
                            ),
                        },
                        ...(canUseViewerActions
                            ? [
                                  {
                                      title: t('admin:projectV1.columns.actions'),
                                      key: 'actions',
                                      width: 380,
                                      fixed: 'right' as const,
                                      render: (_: unknown, record: ViewerAccountSummary) => (
                                          <Space>
                                              {canUpdateProjectViewerAccounts && (
                                                  <Button
                                                      type="link"
                                                      icon={<EditOutlined aria-hidden />}
                                                      onClick={() => openEditModal(record)}
                                                  >
                                                      {t('admin:projectV1.actions.editAccount')}
                                                  </Button>
                                              )}
                                              {canUpdateProjectViewerAccountStatus &&
                                                  (record.status === 'active' ? (
                                                      <Button
                                                          type="link"
                                                          danger
                                                          icon={<StopOutlined aria-hidden />}
                                                          onClick={() => openStatusModal(record)}
                                                      >
                                                          {t('admin:projectV1.actions.disableAccount')}
                                                      </Button>
                                                  ) : (
                                                      <Button
                                                          type="link"
                                                          icon={<CheckCircleOutlined aria-hidden />}
                                                          onClick={() => openStatusModal(record)}
                                                      >
                                                          {t('admin:projectV1.actions.enableAccount')}
                                                      </Button>
                                                  ))}
                                              {canGrantProjectViewers && (
                                                  <Button
                                                      type="link"
                                                      icon={<SafetyCertificateOutlined aria-hidden />}
                                                      onClick={() => openAuthorizationModal(record)}
                                                  >
                                                      {t('admin:projectV1.actions.manageAuthorization')}
                                                  </Button>
                                              )}
                                          </Space>
                                      ),
                                  },
                              ]
                            : []),
                        ]}
                    />
                </PaginatedListCard>
            </Space>

            <Modal
                open={editModalOpen}
                title={t('admin:projectV1.modal.editTitleWithName', {
                    displayName: editingViewer?.displayName || '',
                    username: editingViewer?.username || '',
                })}
                width={640}
                footer={[
                    <Button
                        key="cancel"
                        disabled={submittingEdit}
                        onClick={() => setEditModalOpen(false)}
                    >
                        {t('common:buttons.cancel')}
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={submittingEdit}
                        onClick={() => void handleEditSubmit()}
                    >
                        {t('admin:projectV1.actions.confirmSave')}
                    </Button>,
                ]}
                onCancel={() => {
                    if (!submittingEdit) {
                        setEditModalOpen(false)
                    }
                }}
                maskClosable={false}
                destroyOnClose
            >
                <Form form={editForm} layout="vertical">
                    <Form.Item
                        name="display_name"
                        label={t('admin:projectV1.form.displayName')}
                        rules={[{ required: true }]}
                    >
                        <MaskedReadonlyInput
                            aria-label={t('admin:projectV1.form.displayName')}
                            value={editForm.getFieldValue('display_name')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="username"
                        label={t('admin:projectV1.form.loginAccount')}
                        rules={[{ required: true }]}
                    >
                        <MaskedReadonlyInput
                            aria-label={t('admin:projectV1.form.loginAccount')}
                            value={editForm.getFieldValue('username')}
                        />
                    </Form.Item>
                    <Form.Item
                        name="organization_name"
                        label={t('admin:projectV1.form.organizationUnit')}
                        rules={[
                            {
                                max: 50,
                                message: t('admin:projectV1.form.organizationUnitMaxLength'),
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={authorizationModalOpen}
                title={t('admin:projectV1.modal.authorizationTitle', {
                    displayName: authorizationViewer?.displayName || '',
                    username: authorizationViewer?.username || '',
                })}
                width={860}
                onCancel={() => {
                    if (!submittingAuthorization) {
                        setAuthorizationModalOpen(false)
                    }
                }}
                maskClosable={false}
                destroyOnClose
                footer={[
                    <Button
                        key="select"
                        disabled={submittingAuthorization}
                        onClick={selectFilteredResults}
                    >
                        {t('admin:projectV1.actions.selectFiltered')}
                    </Button>,
                    <Button
                        key="clear"
                        disabled={submittingAuthorization}
                        onClick={clearFilteredResults}
                    >
                        {t('admin:projectV1.actions.clearFiltered')}
                    </Button>,
                    <Button
                        key="close"
                        disabled={submittingAuthorization}
                        onClick={() => setAuthorizationModalOpen(false)}
                    >
                        {t('common:buttons.close')}
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={submittingAuthorization}
                        onClick={() => void handleSaveAuthorization()}
                    >
                        {t('admin:projectV1.actions.saveAuthorization')}
                    </Button>,
                ]}
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Alert
                        type="info"
                        showIcon
                        message={t('admin:projectV1.authorization.notice')}
                    />

                    <FilterBar
                        className="project-authorization-filter-bar"
                        data-testid="project-authorization-filter-panel"
                    >
                        <FilterField
                            label={t('admin:projectV1.authorization.projectLabel')}
                        >
                            <Input
                                allowClear
                                value={projectKeyword}
                                placeholder={t('admin:projectV1.authorization.projectKeyword')}
                                onChange={(event) => setProjectKeyword(event.target.value)}
                                onPressEnter={applyAuthorizationFilters}
                            />
                        </FilterField>
                        <FilterField
                            label={t('admin:projectV1.authorization.regionLabel')}
                        >
                            <Select
                                value={projectRegionFilter}
                                options={[
                                    {
                                        label: t('admin:projectV1.authorization.allRegions'),
                                        value: 'all',
                                    },
                                    ...regionOptions.map((region) => ({
                                        label: region,
                                        value: region,
                                    })),
                                ]}
                                showSearch
                                optionFilterProp="label"
                                filterOption={filterRegionSelectOption}
                                onChange={(value) => setProjectRegionFilter(value)}
                            />
                        </FilterField>
                        <FilterField
                            label={t('admin:projectV1.authorization.statusLabel')}
                        >
                            <Select
                                value={projectStatusFilter}
                                options={[
                                    {
                                        label: t('admin:projectV1.authorization.allProjectStatus'),
                                        value: 'all',
                                    },
                                    {
                                        label: t('admin:projectV1.status.active'),
                                        value: 'active',
                                    },
                                    {
                                        label: t('admin:projectV1.status.inactive'),
                                        value: 'inactive',
                                    },
                                ]}
                                onChange={(value) => setProjectStatusFilter(value)}
                            />
                        </FilterField>
                        <FilterField
                            label={t('admin:projectV1.authorization.authorizationStatusLabel')}
                        >
                            <Select
                                value={authorizationFilter}
                                options={[
                                    {
                                        label: t('admin:projectV1.authorization.allAuthorization'),
                                        value: 'all',
                                    },
                                    {
                                        label: t('admin:projectV1.authorization.authorized'),
                                        value: 'authorized',
                                    },
                                    {
                                        label: t('admin:projectV1.authorization.unauthorized'),
                                        value: 'unauthorized',
                                    },
                                ]}
                                onChange={(value) => setAuthorizationFilter(value)}
                            />
                        </FilterField>
                        <FilterActions
                            align="left"
                            className="project-authorization-filter-actions"
                            testId="project-authorization-filter-actions"
                        >
                            <Button
                                type="primary"
                                icon={<SearchOutlined />}
                                onClick={applyAuthorizationFilters}
                            >
                                {t('common:buttons.search')}
                            </Button>
                            <Button onClick={resetAuthorizationFilters}>
                                {t('common:buttons.reset')}
                            </Button>
                        </FilterActions>
                    </FilterBar>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Typography.Text strong>
                            {t('admin:projectV1.authorization.currentAuthorizedProjects')}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            {t('admin:projectV1.authorization.selectedCount', {
                                count: checkedProjectKeys.length,
                            })}
                        </Typography.Text>
                    </div>

                    <div
                        style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: 12,
                            padding: 16,
                            minHeight: 320,
                            maxHeight: 420,
                            overflow: 'auto',
                            background: '#fff',
                        }}
                    >
                        <Tree
                            checkable
                            selectable={false}
                            checkedKeys={checkedProjectKeys}
                            onCheck={(keys) => {
                                const rawKeys = Array.isArray(keys) ? keys : keys.checked
                                const visibleCheckedProjectKeys = new Set(
                                    rawKeys
                                        .map(String)
                                        .filter((key) => filteredProjectKeySet.has(key))
                                        .filter((key) => allProjectKeySet.has(key))
                                )
                                setCheckedProjectKeys((current) => {
                                    const nextChecked = new Set(
                                        current.filter(
                                            (projectKey) =>
                                                allProjectKeySet.has(projectKey) &&
                                                !filteredProjectKeySet.has(projectKey)
                                        )
                                    )
                                    visibleCheckedProjectKeys.forEach((projectKey) =>
                                        nextChecked.add(projectKey)
                                    )
                                    return Array.from(nextChecked)
                                })
                            }}
                            treeData={treeData}
                            defaultExpandAll
                        />
                    </div>
                </Space>
            </Modal>

            <Modal
                open={statusModal.open}
                footer={null}
                closable={false}
                onCancel={closeStatusModal}
                width={420}
                maskClosable={false}
                destroyOnClose
            >
                <div style={{ display: 'flex', gap: 12 }}>
                    <ExclamationCircleFilled
                        style={{
                            color: '#faad14',
                            fontSize: 22,
                            marginTop: 4,
                        }}
                    />
                    <div style={{ flex: 1 }}>
                        <div
                            style={{
                                fontSize: 16,
                                fontWeight: 600,
                                color: '#262626',
                                lineHeight: 1.5,
                                marginBottom: 12,
                            }}
                        >
                            {statusModalTitle}
                        </div>
                        <div
                            style={{
                                fontSize: 16,
                                color: '#595959',
                                lineHeight: 1.75,
                                marginBottom: 24,
                            }}
                        >
                            {statusModalDescription}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 12,
                            }}
                        >
                            <Button
                                onClick={closeStatusModal}
                                disabled={statusModal.submitting || submittingStatus}
                            >
                                {t('common:buttons.cancel')}
                            </Button>
                            <Button
                                type="primary"
                                danger={statusModal.targetStatus === 'inactive'}
                                loading={statusModal.submitting || submittingStatus}
                                onClick={() => void handleToggleStatus()}
                            >
                                {statusActionLabel}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    )

    if (embedded) {
        return content
    }

    return (
        <PageLayout
            title={t('admin:projectV1.title')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.projectManagement') },
                { title: t('navigation:breadcrumb.projectViewerScopes') },
            ]}
        >
            {content}
        </PageLayout>
    )
}

export default ProjectViewerScopes
